import React, {useContext, useState} from 'react';
import {
    Box, Header, Button, ButtonDropdown, Alert, Link, StatusIndicator, SpaceBetween
} from '@amzn/awsui-components-react';
import {TableProps} from "@amzn/awsui-components-react/polaris/table";
import TPPolarisTable from '../shared/TPPolarisTable';
import {
    MultiAllocationSheetData, WorksheetStatus
} from 'src/models/multidimensional-allocation/MultiAllocationSheetData';
import {
    DerivedAllocationSheetData
} from 'src/models/derived-allocation-sheet/DerivedAllocationSheet';
import {
    SheetKind, SHEET_KIND_LABELS
} from 'src/models/multidimensional-allocation/SheetKind';
import {
    MultidimensionalAllocationContext
} from 'src/services/multidimensional-allocation/MultidimensionalAllocationContext';
import {
    SHEET_COLUMN_DEFINITIONS,
    SHEET_DEFAULT_COLLECTION_PREFERENCES,
    SHEET_FILTERING_PROPERTIES,
    SHEET_VISIBLE_CONTENT_OPTIONS,
    STATUS_INDICATOR_CONFIG,
    PAGE_SIZE_OPTIONS,
} from 'src/components/multidimensional-allocation/common/Constants';
import UploadMDASheetModal from './sheets/UploadMDASheetModal';
import CreateDerivedAllocationSheetModal from './sheets/CreateDerivedAllocationSheetModal';
import { getPermissions } from 'src/components/AppPermissions';
import { AppModules } from 'src/models/permissions/RolePermissions';
import { isDerivedMdaAllocationEnabled } from 'src/services/FeatureFlagsService';
import CONSTANTS from 'src/utils/constants';

interface UnifiedSheetRow {
    sheetId: string;
    sheetName: string;
    type: SheetKind;
    status: WorksheetStatus;
    /** Null on a derived sheet until Step 4 is saved. */
    segmentType?: string;
    lastUpdatedAt: string;
    createdBy: string;
}

/**
 * Component for managing allocation sheets. Shows manual-upload sheets and derived allocation
 * sheets in a single table with a Type column. Derived rows are only fetched when the
 * `derivedMDAAllocationEnabled` feature flag is on.
 */
export default function ManageAllocationSheets() {

    const {services} = useContext(MultidimensionalAllocationContext);
    const {multidimensionalAllocationService, derivedAllocationSheetService} = services;

    // Synchronous flag read avoids the first-render flicker where the create control briefly
    // shows the single Manual button before flipping to the dropdown once the async promise resolves.
    const derivedEnabled = isDerivedMdaAllocationEnabled();

    const [manualSheets, manualLoading, manualError] = multidimensionalAllocationService.listMultiAllocationSheets('fetch');
    const [derivedSheets, derivedLoading, derivedError] =
        derivedAllocationSheetService.listDerivedAllocationSheets(derivedEnabled ? 'fetch' : '');

    const [createModalVisible, setCreateModalVisible] = useState(false);
    const [createDerivedModalVisible, setCreateDerivedModalVisible] = useState(false);
    const { canEdit } = getPermissions(AppModules.MULTIDIMENSIONAL_ALLOCATION);

    const rows: UnifiedSheetRow[] = [
        ...(manualSheets?.sheets ?? []).map(toManualRow),
        ...(derivedEnabled ? (derivedSheets?.sheets ?? []).map(toDerivedRow) : []),
    ];

    const loading = manualLoading || (derivedEnabled && derivedLoading);
    const error = manualError || (derivedEnabled ? derivedError : '');

    const columnDefinitions: TableProps.ColumnDefinition<UnifiedSheetRow>[] = [{
        ...SHEET_COLUMN_DEFINITIONS.NAME,
        cell: (item) => (
            <Link href={detailHref(item)}>
                {item.sheetName}
            </Link>),
        isRowHeader: true,
    }, {
        ...SHEET_COLUMN_DEFINITIONS.TYPE, cell: (item) => SHEET_KIND_LABELS[item.type],
    }, {
        ...SHEET_COLUMN_DEFINITIONS.STATUS, cell: (item) => getStatusIndicator(item.status),
    }, {
        ...SHEET_COLUMN_DEFINITIONS.SEGMENT_TYPE, cell: (item) => item.segmentType,
    }, {
        ...SHEET_COLUMN_DEFINITIONS.UPLOADED_AT, cell: (item) => item.lastUpdatedAt,
    }, {
        ...SHEET_COLUMN_DEFINITIONS.UPLOADED_BY, cell: (item) => item.createdBy,
    }];

    const createAction = derivedEnabled
        ? <ButtonDropdown
            variant="primary"
            items={[
                { id: SheetKind.MANUAL_UPLOAD, text: 'Manual upload' },
                { id: SheetKind.DERIVED_RATIO, text: 'Derived ratio' },
            ]}
            onItemClick={({ detail }) => {
                if (detail.id === SheetKind.MANUAL_UPLOAD) {
                    setCreateModalVisible(true);
                } else if (detail.id === SheetKind.DERIVED_RATIO) {
                    setCreateDerivedModalVisible(true);
                }
            }}
          >
            Create allocation sheet
          </ButtonDropdown>
        : <Button variant='primary' onClick={() => setCreateModalVisible(true)}>
            Create multidimensional allocation sheet
          </Button>;

    const tableEmptyState = error ? <Box textAlign="center">
        <Alert type="error" header="Unable to load sheets"
               action={<Button onClick={() => window.location.reload()}>Refresh </Button>}>
            {error}
        </Alert>
    </Box> : <Box textAlign="center">
        <SpaceBetween direction='vertical' size='s'>
            <b>No allocation sheets found</b>
            {canEdit && "Get started by creating your first allocation sheet."}
            {canEdit && createAction}
        </SpaceBetween>
    </Box>;

    const header = <Header actions={canEdit ? createAction : undefined}>
                    Multidimensional Allocation Sheets
                </Header>;

    return (<>
                <UploadMDASheetModal visible={createModalVisible}
                                     onHide={() => setCreateModalVisible(false)}
                />
                <CreateDerivedAllocationSheetModal visible={createDerivedModalVisible}
                                                   onHide={() => setCreateDerivedModalVisible(false)}
                />

                <TPPolarisTable
                    header={header}
                    columnDefinitions={columnDefinitions}
                    tableItems={rows}
                    isLoading={loading}
                    tableEmptyState={tableEmptyState}
                    resizableColumns={true}
                    sortingColumn="sheetName"
                    filteringProperties={SHEET_FILTERING_PROPERTIES}
                    filteringEmptyText="No sheets match the filter criteria"
                    filteringLoadingText="Loading sheets..."
                    preferencesStorageKey="mda-sheet-table-preferences"
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    visibleContentOptions={SHEET_VISIBLE_CONTENT_OPTIONS}
                    defaultPreferences={SHEET_DEFAULT_COLLECTION_PREFERENCES}
                />
        </>
    );
}

function toManualRow(sheet: MultiAllocationSheetData): UnifiedSheetRow {
    return {
        sheetId: sheet.sheetId,
        sheetName: sheet.sheetName,
        type: SheetKind.MANUAL_UPLOAD,
        status: sheet.status,
        segmentType: sheet.segmentType,
        lastUpdatedAt: sheet.lastUpdatedAt,
        createdBy: sheet.createdBy,
    };
}

function toDerivedRow(sheet: DerivedAllocationSheetData): UnifiedSheetRow {
    return {
        sheetId: sheet.sheetId,
        sheetName: sheet.sheetName,
        type: SheetKind.DERIVED_RATIO,
        status: sheet.status,
        segmentType: sheet.segmentType,
        lastUpdatedAt: sheet.lastModifiedDate,
        createdBy: sheet.createdBy,
    };
}

function detailHref(item: UnifiedSheetRow): string {
    const base = item.type === SheetKind.DERIVED_RATIO
        ? CONSTANTS.PAGE_NAV.MULTIDIMENSIONAL_ALLOCATION_DERIVED_SHEET_DETAILS.URL
        : CONSTANTS.PAGE_NAV.MULTIDIMENSIONAL_ALLOCATION_SHEET_DETAILS.URL;
    return `${base}/${item.sheetId}`;
}

function getStatusIndicator(status: string) {
    const [label, statusType, color] = STATUS_INDICATOR_CONFIG[status as WorksheetStatus];
    return <StatusIndicator type={statusType} colorOverride={color}>
        {label}
    </StatusIndicator>;
}
